// src/features/auth/hooks/use-referral.ts
import { useState, useCallback, useRef, useEffect } from "react";
import type { RewardItem, ReferralCallbacks } from "../auth.types";
import defaultRewardsData from "../data/rewards.json";
import { useAudioEffects } from "../../../hooks/use-audio-effects";
import successRewardSound from "../assets/audio/success-reward.wav";

export type ReferralStatus = "idle" | "loading" | "success" | "error";

interface UseReferralOptions extends ReferralCallbacks {}

interface UseReferralReturn {
  code: string;
  status: ReferralStatus;
  error: string | null;
  rewards: RewardItem[];
  isLoading: boolean;
  isSuccess: boolean;
  handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleSubmit: () => Promise<void>;
  handleKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
}

export const useReferral = ({
  onSuccess,
  onError,
}: UseReferralOptions = {}): UseReferralReturn => {
  const [code, setCode] = useState("");
  const [status, setStatus] = useState<ReferralStatus>("idle");
  const [error, setError] = useState<string | null>(null);
  const [rewards, setRewards] = useState<RewardItem[]>([]);
  const { playEffect } = useAudioEffects();

  // Stable refs — callbacks won't invalidate useCallback deps
  const onSuccessRef = useRef(onSuccess);
  const onErrorRef = useRef(onError);
  useEffect(() => {
    onSuccessRef.current = onSuccess;
  }, [onSuccess]);
  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setCode(e.target.value);
      if (status === "error") {
        setStatus("idle");
        setError(null);
      }
    },
    [status],
  );

  const handleSubmit = useCallback(async () => {
    if (status === "loading") return;

    if (!code) {
      setStatus("error");
      setError("Code is missing");
      return;
    }

    setStatus("loading");
    setError(null);

    try {
      // TODO: replace with real API call, e.g.:
      // const data = await apiClient.post("/referral/validate", { code });
      await new Promise((res) => setTimeout(res, 2000));

      const data = { rewards: defaultRewardsData as RewardItem[] };
      setRewards(data.rewards);
      setStatus("success");
      playEffect(successRewardSound);
      onSuccessRef.current?.(data);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Cod de referral invalid.";
      setStatus("error");
      setError(message);
      onErrorRef.current?.(message);
    }
  }, [code, status, playEffect]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") handleSubmit();
    },
    [handleSubmit],
  );

  return {
    code,
    status,
    error,
    rewards,
    isLoading: status === "loading",
    isSuccess: status === "success",
    handleChange,
    handleSubmit,
    handleKeyDown,
  };
};
